// RAD customisations for the Epitome theme.

/*
  Task 12: International shipping
  Settings come from the [data-rad-intl] wrapper (sections/rad-international.liquid), never from the modal,
  because Epitome's modal-dialog moves itself to <body>. Fails open: if detection fails, the visitor is treated
  as supported, so an error never shows the modal or changes the product page.
*/
(function () {
  'use strict';

  var COUNTRY_KEY = 'rad_intl_country';
  var DISMISSED_KEY = 'rad_intl_dismissed';
  var MODAL_ID = 'RadIntlModal';

  function getWrapper() {
    return document.querySelector('[data-rad-intl]');
  }

  function getModal() {
    return document.getElementById(MODAL_ID);
  }

  function readSettings(wrapper) {
    var data = wrapper.dataset;
    return {
      supportedCountries: (data.supportedCountries || '')
        .split(',')
        .map(function (code) {
          return code.trim().toUpperCase();
        })
        .filter(Boolean),
      enableModal: data.enableModal === 'true',
      delay: Math.max(0, parseFloat(data.delay) || 0) * 1000,
      klaviyoCompany: data.klaviyoCompany || '',
      klaviyoList: data.klaviyoList || '',
      allowOverride: data.allowOverride === 'true',
      preview: data.preview === 'true',
      successMessage: data.successMessage || '',
    };
  }

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Storage unavailable (private mode or blocked): carry on without it
    }
  }

  function showModal(modal) {
    if (modal.hasAttribute('open')) return;

    var closeButton = modal.querySelector('[id^="ModalClose-"]');

    if (typeof modal.show === 'function') {
      modal.show(document.activeElement || document.body);
    } else {
      modal.setAttribute('open', '');
    }

    // No transitionend with reduced motion or in a background tab: move focus in so Esc and the focus trap still work
    window.setTimeout(function () {
      if (!modal.hasAttribute('open') || modal.contains(document.activeElement)) return;
      var focusTarget = closeButton || modal.querySelector('[role="dialog"]');
      if (!focusTarget) return;
      if (typeof window.trapFocus === 'function') {
        window.trapFocus(modal, focusTarget);
      } else {
        focusTarget.focus();
      }
    }, 600);
  }

  function hideModal(modal) {
    if (typeof modal.hide === 'function') {
      modal.hide();
    } else {
      modal.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }
  }

  var wrapper = getWrapper();
  if (!wrapper) return;

  var settings = readSettings(wrapper);

  // Theme editor: no detection, no automatic modal. Keep one modal in <body> and open it while the section is selected
  if (window.Shopify && window.Shopify.designMode) {
    // Same API shape for later code, always supported so nothing changes on product pages in the editor
    window.RADInternational = {
      ready: Promise.resolve({ handle: null, name: null, supported: true }),
      openModal: function () {
        var modal = getModal();
        if (modal) showModal(modal);
      },
    };

    var isOwnSection = function (event) {
      return event.target && event.target.querySelector && event.target.querySelector('[data-rad-intl]');
    };

    document.addEventListener('shopify:section:load', function (event) {
      if (!isOwnSection(event)) return;

      // The new modal-dialog moves itself to <body> as soon as it connects, so it is usually the last one there
      var modals = Array.prototype.slice.call(document.querySelectorAll('modal-dialog#' + MODAL_ID));
      var keep = event.target.querySelector('modal-dialog#' + MODAL_ID) || modals[modals.length - 1];
      modals.forEach(function (modal) {
        if (modal !== keep) modal.remove();
      });
      if (!document.querySelector('modal-dialog#' + MODAL_ID + '[open]')) {
        document.body.classList.remove('overflow-hidden');
      }

      wrapper = getWrapper();
      if (wrapper) settings = readSettings(wrapper);
    });

    document.addEventListener('shopify:section:select', function (event) {
      if (!isOwnSection(event)) return;
      var modal = getModal();
      if (modal) showModal(modal);
    });

    document.addEventListener('shopify:section:deselect', function (event) {
      if (!isOwnSection(event)) return;
      var modal = getModal();
      if (modal) hideModal(modal);
    });

    return;
  }

  function readCachedCountry() {
    try {
      var cached = JSON.parse(storageGet(COUNTRY_KEY));
      if (cached && cached.handle) return { handle: String(cached.handle), name: String(cached.name || cached.handle) };
    } catch (error) {
      // Ignore a malformed cache entry and detect again
    }
    return null;
  }

  function readOverrideCountry() {
    if (!settings.allowOverride) return null;
    var code;
    try {
      code = new URLSearchParams(window.location.search).get('rad_country');
    } catch (error) {
      return null;
    }
    if (!code || !/^[a-z]{2}$/i.test(code.trim())) return null;
    code = code.trim().toUpperCase();
    return { handle: code, name: code };
  }

  function fetchCountry() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'browsing_context_suggestions.json?country[enabled]=true', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (json) {
        var country = json && json.detected_values && json.detected_values.country;
        if (!country || !country.handle) return null;
        return { handle: String(country.handle).toUpperCase(), name: String(country.name || country.handle) };
      });
  }

  function detectCountry() {
    var override = readOverrideCountry();
    if (override) {
      storageSet(COUNTRY_KEY, JSON.stringify(override));
      return Promise.resolve(override);
    }

    var cached = readCachedCountry();
    if (cached) return Promise.resolve(cached);

    return fetchCountry().then(function (country) {
      if (country) storageSet(COUNTRY_KEY, JSON.stringify(country));
      return country;
    });
  }

  var ready = detectCountry()
    .then(function (country) {
      if (!country) return { handle: null, name: null, supported: true };
      return {
        handle: country.handle,
        name: country.name,
        supported: settings.supportedCountries.indexOf(country.handle) !== -1,
      };
    })
    .catch(function (error) {
      console.warn('[ROMY international] Country detection failed, treating visitor as supported.', error && error.message);
      return { handle: null, name: null, supported: true };
    });

  var detectedName = '';
  var detectedHandle = '';
  ready.then(function (result) {
    if (result.name) detectedName = result.name;
    if (result.handle) detectedHandle = result.handle;
  });

  function getForm(modal) {
    return modal ? modal.querySelector('.rad-intl-form') : null;
  }

  function prefillCountry(form) {
    var countryInput = form.querySelector('input[name="country"]');
    if (countryInput && !countryInput.value && detectedName) countryInput.value = detectedName;
  }

  function prepareModal(modal, source) {
    var form = getForm(modal);
    if (!form) return;
    form.dataset.source = source;
    prefillCountry(form);
    // Opened before detection finished: fill the country in once it arrives
    if (!detectedName) {
      ready.then(function () {
        prefillCountry(form);
      });
    }
  }

  function openModal(source) {
    var modal = getModal();
    if (!modal) return;
    prepareModal(modal, source === 'pdp' ? 'pdp' : 'modal');
    showModal(modal);
  }

  window.RADInternational = { ready: ready, openModal: openModal };

  var modal = getModal();

  if (modal) {
    // Any close (button, Esc, backdrop, dismiss) removes the open attribute: remember it for the session
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.oldValue !== null && !modal.hasAttribute('open')) storageSet(DISMISSED_KEY, '1');
      });
    }).observe(modal, { attributes: true, attributeFilter: ['open'], attributeOldValue: true });

    var dismissButton = modal.querySelector('[data-rad-intl-dismiss]');
    if (dismissButton) {
      dismissButton.addEventListener('click', function () {
        hideModal(modal);
      });
    }

    var form = getForm(modal);
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        submitInterest(modal, form);
      });
    }
  }

  // Klaviyo client subscription (public key only, no SDK). Key and list ID come from the wrapper's data attributes
  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2024-10-15';
  var MESSAGE_INVALID_EMAIL = 'Please enter a valid email address.';
  var MESSAGE_ERROR = 'Something went wrong, please try again.';
  var warnedMissingKlaviyo = false;

  function setFormError(form, message) {
    var error = form.querySelector('.rad-intl-form__error');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  function buildSubscription(list, email, country, source) {
    var properties = {};
    if (country) properties['International Interest Country'] = country;
    if (detectedHandle) properties['International Interest Country Code'] = detectedHandle;
    properties['International Signup Source'] = source;
    properties['International Signup Date'] = new Date().toISOString();

    var profileAttributes = { email: email };
    if (country) profileAttributes.location = { country: country };
    profileAttributes.properties = properties;

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'ROMY international shipping interest',
          profile: { data: { type: 'profile', attributes: profileAttributes } },
        },
        relationships: { list: { data: { type: 'list', id: list } } },
      },
    };
  }

  function submitInterest(modal, form) {
    var emailInput = form.querySelector('input[name="email"]');
    var countryInput = form.querySelector('input[name="country"]');
    var submitButton = form.querySelector('[type="submit"]');
    if (submitButton && submitButton.disabled) return;

    setFormError(form, '');

    if (!emailInput || !emailInput.checkValidity()) {
      if (emailInput) {
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
      }
      setFormError(form, MESSAGE_INVALID_EMAIL);
      return;
    }
    emailInput.removeAttribute('aria-invalid');

    var current = readSettings(getWrapper() || wrapper);
    var company = current.klaviyoCompany.trim();
    var list = current.klaviyoList.trim();
    if (!company || !list) {
      if (!warnedMissingKlaviyo) {
        console.warn('[ROMY international] Klaviyo public API key or list ID is blank in the International shipping section.');
        warnedMissingKlaviyo = true;
      }
      setFormError(form, MESSAGE_ERROR);
      return;
    }

    var payload = buildSubscription(
      list,
      emailInput.value.trim(),
      countryInput ? countryInput.value.trim() : '',
      form.dataset.source === 'pdp' ? 'pdp' : 'modal'
    );

    function setBusy(busy) {
      if (submitButton) submitButton.disabled = busy;
      if (busy) {
        form.setAttribute('aria-busy', 'true');
      } else {
        form.removeAttribute('aria-busy');
      }
    }

    setBusy(true);

    fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION,
      },
      body: JSON.stringify(payload),
    })
      .then(function (response) {
        if (response.status !== 202) throw new Error('HTTP ' + response.status);

        form.removeAttribute('aria-busy');
        // .rad-intl-form sets display: flex, which beats the hidden attribute, so hide it inline as well.
        // The dismiss link goes with the form; the close X (outside the form) is the way out from here
        form.hidden = true;
        form.style.display = 'none';
        storageSet(DISMISSED_KEY, '1');

        // The focused submit button is now hidden: move focus to the message so it is read out and the focus
        // trap still has a starting point. Fall back to the close X if the message is missing
        var success = modal.querySelector('.rad-intl-form__success');
        var closeButton = modal.querySelector('[id^="ModalClose-"]');
        if (success) {
          success.hidden = false;
          success.setAttribute('tabindex', '-1');
          success.focus();
        } else if (closeButton) {
          closeButton.focus();
        }
      })
      .catch(function (error) {
        console.warn('[ROMY international] Klaviyo subscription failed.', error && error.message);
        setBusy(false);
        setFormError(form, MESSAGE_ERROR);
      });
  }

  // One delegated listener, so product page messages inserted later still open the modal
  document.addEventListener('click', function (event) {
    var opener = event.target.closest && event.target.closest('[data-rad-intl-open]');
    if (!opener) return;
    event.preventDefault();
    openModal(opener.getAttribute('data-rad-intl-open'));
  });

  ready.then(function (result) {
    if (!modal || !settings.enableModal || result.supported) return;
    if (storageGet(DISMISSED_KEY) === '1') return;

    window.setTimeout(function () {
      if (storageGet(DISMISSED_KEY) === '1') return;
      openModal('modal');
    }, settings.delay);
  });

  // Product page: for unsupported visitors, an in-stock variant swaps Add to Cart (buy block and sticky bar) for the
  // shipping-interest CTA. Sold out or unavailable: Add to Cart comes back so SOLD OUT and the back-in-stock app work.
  // Hiding uses a class with !important, because the back-in-stock app writes inline display styles on [name="add"].
  // The theme owns the disabled attribute; this code only reads it.
  var HIDDEN_CLASS = 'rad-intl-hidden';
  var CTA_PRIMARY_CLASSES = ['button', 'button--primary', 'rad-intl-cta--primary'];

  function setCtaPrimary(cta, primary) {
    if (!cta.dataset.radLabel) cta.dataset.radLabel = cta.textContent.trim();
    var label = cta.dataset.radLabel;
    CTA_PRIMARY_CLASSES.forEach(function (name) {
      cta.classList.toggle(name, primary);
    });

    var wrapper = cta.querySelector('.button-wrapper-anim');
    if (primary && !wrapper) {
      // Epitome's primary button markup, so it matches Add to Cart
      wrapper = document.createElement('div');
      wrapper.className = 'button-wrapper-anim';
      var text = document.createElement('span');
      text.textContent = label;
      var overlay = document.createElement('div');
      overlay.className = 'button-overlay-anim';
      wrapper.appendChild(text);
      wrapper.appendChild(overlay);
      cta.textContent = '';
      cta.appendChild(wrapper);
    } else if (!primary && wrapper) {
      cta.textContent = label;
    }
  }

  function setupPdpMessage(placeholder, template) {
    var form = placeholder.closest('form');
    var addButton = form ? form.querySelector('[name="add"]') : null;
    if (!addButton) return;

    if (!placeholder.hasChildNodes()) placeholder.appendChild(template.content.cloneNode(true));
    // The snippet renders with the hidden attribute; from here on visibility is the class
    placeholder.classList.add(HIDDEN_CLASS);
    placeholder.hidden = false;

    var cta = placeholder.querySelector('[data-rad-intl-open]');
    // getAttribute, because form.id returns the form's <input name="id">
    var sectionId = (form.getAttribute('id') || '').replace(/^product-form-/, '');
    var stickyCta = null;
    var observedSticky = null;
    var variantJustPublished = false;

    function getStickyForm() {
      return document.getElementById('sticky-bar-product-form-' + sectionId);
    }

    function getStickyButton() {
      var stickyForm = getStickyForm();
      return stickyForm ? stickyForm.querySelector('[name="add"]') : null;
    }

    function hidePaymentButtons() {
      [form, getStickyForm()].forEach(function (scope) {
        if (!scope) return;
        Array.prototype.forEach.call(scope.querySelectorAll('.shopify-payment-button'), function (element) {
          element.classList.add(HIDDEN_CLASS);
        });
      });
    }

    function applyBuyBlock(available) {
      addButton.classList.toggle(HIDDEN_CLASS, available);
      placeholder.classList.toggle(HIDDEN_CLASS, !available);
      if (cta) setCtaPrimary(cta, available);
    }

    function applySticky(available) {
      var stickyButton = getStickyButton();
      if (!stickyButton) return;
      watchSticky(stickyButton);

      if (!available) {
        stickyButton.classList.remove(HIDDEN_CLASS);
        if (stickyCta) stickyCta.classList.add(HIDDEN_CLASS);
        return;
      }

      if (!stickyCta && cta) {
        stickyCta = cta.cloneNode(true);
        setCtaPrimary(stickyCta, true);
        stickyCta.classList.add('rad-intl-cta--sticky');
      }
      if (!stickyCta) return;
      if (stickyButton.nextElementSibling !== stickyCta) stickyButton.insertAdjacentElement('afterend', stickyCta);
      stickyButton.classList.add(HIDDEN_CLASS);
      stickyCta.classList.remove(HIDDEN_CLASS);
    }

    function refresh() {
      applyBuyBlock(!addButton.hasAttribute('disabled'));
      var stickyButton = getStickyButton();
      if (stickyButton) applySticky(!stickyButton.hasAttribute('disabled'));
      hidePaymentButtons();
    }

    function refreshSticky() {
      var stickyButton = getStickyButton();
      if (stickyButton) applySticky(!stickyButton.hasAttribute('disabled'));
    }

    // Both observers skip the theme's transient disable while a new variant loads (same task as the publish)
    function watchSticky(stickyButton) {
      if (observedSticky === stickyButton) return;
      observedSticky = stickyButton;
      new MutationObserver(function () {
        if (variantJustPublished) return;
        refreshSticky();
      }).observe(stickyButton, { attributes: true, attributeFilter: ['disabled'] });
    }

    refresh();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var data = event && event.data;
        if (!data || data.sectionId !== sectionId || !data.variant) return;
        var available = data.variant.available === true;

        // Sold out: show Add to Cart again before anything else, so the back-in-stock app finds [name="add"]:visible
        if (!available) {
          addButton.classList.remove(HIDDEN_CLASS);
          var stickyButton = getStickyButton();
          if (stickyButton) stickyButton.classList.remove(HIDDEN_CLASS);
        }

        applyBuyBlock(available);
        applySticky(available);

        variantJustPublished = true;
        window.setTimeout(function () {
          variantJustPublished = false;
        }, 0);
      });
    }

    // Catches every later change, including option combinations with no variant, which publish nothing
    new MutationObserver(function () {
      if (variantJustPublished) return;
      refresh();
    }).observe(addButton, { attributes: true, attributeFilter: ['disabled'] });
  }

  var pdpPlaceholders = Array.prototype.slice.call(document.querySelectorAll('[data-rad-intl-pdp]'));

  if (pdpPlaceholders.length) {
    ready.then(function (result) {
      if (result.supported) return;
      var template = document.getElementById('rad-intl-pdp-template');
      if (!template) return;
      pdpPlaceholders.forEach(function (placeholder) {
        setupPdpMessage(placeholder, template);
      });
    });
  }
})();

/*
  Task 15: SHOP THE LOOK
  Arrows for the [data-rad-stl] rows (snippets/rad-shop-the-look-cart.liquid). The cart drawer is rerendered whole on
  every cart update, so the arrow state and the international hiding are reapplied after each rerender. Adding to
  cart is Epitome's own <product-form>, nothing here.
*/
(function () {
  'use strict';

  var hideForCountry = false;

  function getTrack(row) {
    return row.querySelector('[data-rad-stl-track]');
  }

  // One card's width plus the track gap, so each click moves exactly one card
  function getStep(track) {
    var card = track.querySelector('[data-rad-stl-card]');
    if (!card) return track.clientWidth;
    var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function setDisabled(button, disabled) {
    if (!button) return;
    button.classList.toggle('is-disabled', disabled);
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  function updateRow(row) {
    var track = getTrack(row);
    if (!track) return;
    var prev = row.querySelector('[data-rad-stl-prev]');
    var next = row.querySelector('[data-rad-stl-next]');
    var maxScroll = track.scrollWidth - track.clientWidth;
    var overflows = maxScroll > 1;

    [prev, next].forEach(function (button) {
      if (button) button.classList.toggle('rad-intl-hidden', !overflows);
    });
    setDisabled(prev, !overflows || track.scrollLeft <= 1);
    setDisabled(next, !overflows || track.scrollLeft >= maxScroll - 1);
  }

  function bindTrack(row) {
    var track = getTrack(row);
    // A rerendered track is a new element without the marker, so it gets its own listener
    if (!track || track.hasAttribute('data-rad-stl-bound')) return;
    track.setAttribute('data-rad-stl-bound', '');
    track.addEventListener(
      'scroll',
      function () {
        updateRow(row);
      },
      { passive: true }
    );
  }

  function refresh() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-rad-stl]'), function (row) {
      if (hideForCountry) row.classList.add('rad-intl-hidden');
      bindTrack(row);
      updateRow(row);
    });
  }

  var frame = null;
  function scheduleRefresh() {
    if (frame) return;
    frame = window.requestAnimationFrame(function () {
      frame = null;
      refresh();
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-rad-stl-prev], [data-rad-stl-next]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    var row = button.closest('[data-rad-stl]');
    var track = row && getTrack(row);
    if (!track) return;
    var direction = button.hasAttribute('data-rad-stl-prev') ? -1 : 1;
    track.scrollBy({ left: direction * getStep(track), behavior: 'smooth' });
  });

  window.addEventListener('resize', scheduleRefresh);

  // Cart updates replace the drawer's inner HTML (cart-drawer.js renderContents, cart.js updateQuantity)
  var drawer = document.querySelector('cart-drawer');
  if (drawer && window.MutationObserver) {
    new MutationObserver(scheduleRefresh).observe(drawer, { childList: true, subtree: true });
  }

  refresh();

  // International (Task 12): unsupported visitors can't order, so the row is hidden. Supported, failed detection or
  // no Task 12 section: the row stays
  var international = window.RADInternational;
  if (international && international.ready && typeof international.ready.then === 'function') {
    international.ready.then(function (result) {
      if (result && result.supported === false) {
        hideForCountry = true;
        refresh();
      }
    });
  }
})();

/*
  Task 13: sign-up fallback
  With a Klaviyo form ID the footer outputs Epitome's Shopify form as .rad-signup-fallback[hidden] after the
  .rad-klaviyo-signup block (sections/footer.liquid). If Klaviyo has not rendered into its .klaviyo-form-* mount
  4 seconds after load (embed off, form unpublished, blocked script), the fallback is shown so the footer always
  has an email field. Rechecked when the footer is re-rendered in the theme editor.
*/
(function () {
  'use strict';

  var FALLBACK_DELAY = 4000;

  function showFallbacks() {
    var signups = document.querySelectorAll('.rad-klaviyo-signup');
    Array.prototype.forEach.call(signups, function (signup) {
      var fallback = signup.parentNode && signup.parentNode.querySelector('.rad-signup-fallback');
      if (!fallback || !fallback.hidden) return;
      var mount = signup.querySelector('[class^="klaviyo-form-"], [class*=" klaviyo-form-"]');
      if (!mount || mount.children.length === 0) fallback.hidden = false;
    });
  }

  function scheduleCheck() {
    window.setTimeout(showFallbacks, FALLBACK_DELAY);
  }

  if (document.readyState === 'complete') {
    scheduleCheck();
  } else {
    window.addEventListener('load', scheduleCheck);
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('.rad-klaviyo-signup')) scheduleCheck();
  });
})();


/* Keep cart SHOP THE LOOK size selection separate from PDP variant controls. */
(function () {
  var formSelector =
    '.rad-stl--cart .rad-stl-card .rad-stl-card__form form';

  function syncVariant(form) {
    if (!(form instanceof HTMLFormElement) || !form.matches(formSelector)) return;

    var select = form.querySelector('.rad-stl-card__select');
    var input = form.querySelector('input[type="hidden"][name="id"]');

    if (select && input) input.value = select.value;
  }

  document.addEventListener(
    'change',
    function (event) {
      if (!(event.target instanceof HTMLSelectElement)) return;
      if (!event.target.matches('.rad-stl-card__select')) return;

      syncVariant(event.target.closest('form'));
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      syncVariant(event.target);
    },
    true
  );
})();